import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { useAuth, API } from '../contexts/AuthContext';
import Tooltip from '../components/Tooltip';

const COR = '#5C3D52';
const fmt = v => parseFloat(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtPct = v => v == null ? '—' : `${v >= 0 ? '+' : ''}${v.toFixed(1)}%`;
const corPct = v => v == null ? '#888' : v >= 0 ? '#28a745' : '#dc3545';

const MESES = [
  { v: 1, l: 'Janeiro' }, { v: 2, l: 'Fevereiro' }, { v: 3, l: 'Março' },
  { v: 4, l: 'Abril' }, { v: 5, l: 'Maio' }, { v: 6, l: 'Junho' },
  { v: 7, l: 'Julho' }, { v: 8, l: 'Agosto' }, { v: 9, l: 'Setembro' },
  { v: 10, l: 'Outubro' }, { v: 11, l: 'Novembro' }, { v: 12, l: 'Dezembro' },
];

function ultimoDiaDoMes(mes) {
  return new Date(2024, mes, 0).getDate(); // ano 2024 (bissexto) cobre 29/fev
}

export default function AnalisesVenda() {
  const { user } = useAuth();
  const [filiais, setFiliais] = useState([]);

  // Filtros
  const hoje = new Date();
  const [modoData, setModoData] = useState('mes'); // 'mes' | 'datas'
  const [mesIni, setMesIni] = useState(hoje.getMonth() + 1);
  const [mesFim, setMesFim] = useState(hoje.getMonth() + 1);
  // Datas específicas (DD/MM): no formato MM-DD pra bater com backend
  const [diaIni, setDiaIni] = useState(1);
  const [diaFim, setDiaFim] = useState(ultimoDiaDoMes(hoje.getMonth() + 1));
  const [qtdAnos, setQtdAnos] = useState(1);
  const [filiaisSel, setFiliaisSel] = useState([]); // vazio = todas
  const [classes, setClasses] = useState([]); // bdenter classes
  const [classesSel, setClassesSel] = useState([]); // selecionadas

  // Multi-select classes
  const [classeInput, setClasseInput] = useState('');
  const [classeDropOpen, setClasseDropOpen] = useState(false);
  const classeRef = useRef(null);

  // Resultados
  const [cidades, setCidades] = useState(null);
  const [bairrosExpand, setBairrosExpand] = useState({}); // { cidade: dados }
  const [clientes, setClientes] = useState(null);
  const [vendaClasses, setVendaClasses] = useState(null);
  const [loading, setLoading] = useState({});

  const isGerenteFilial = !!user?.filial_id;

  useEffect(() => { axios.get(`${API}/filiais`).then(r => setFiliais(r.data)).catch(() => {}); }, []);
  useEffect(() => { axios.get(`${API}/produtos/classes`).then(r => setClasses(r.data)).catch(() => {}); }, []);

  // Click fora do dropdown classe
  useEffect(() => {
    const onMouseDown = (e) => { if (classeRef.current && !classeRef.current.contains(e.target)) setClasseDropOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setClasseDropOpen(false); };
    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onMouseDown); document.removeEventListener('keydown', onKey); };
  }, []);

  const params = useMemo(() => {
    const dIni = modoData === 'mes' ? 1 : diaIni;
    const dFim = modoData === 'mes' ? ultimoDiaDoMes(mesFim) : diaFim;
    const p = {
      qtdAnos,
      dataIni: `${String(mesIni).padStart(2, '0')}-${String(dIni).padStart(2, '0')}`,
      dataFim: `${String(mesFim).padStart(2, '0')}-${String(dFim).padStart(2, '0')}`,
    };
    if (!isGerenteFilial && filiaisSel.length) p.filiais = filiaisSel.join(',');
    if (classesSel.length) p.classes = classesSel.join(',');
    return p;
  }, [qtdAnos, mesIni, mesFim, diaIni, diaFim, modoData, filiaisSel, classesSel, isGerenteFilial]);

  // Atalhos rápidos
  const aplicarAtalho = (chave) => {
    const m = hoje.getMonth() + 1;
    setModoData('mes');
    if (chave === 'mes_atual') { setMesIni(m); setMesFim(m); }
    else if (chave === 'mes_passado') { const mp = m === 1 ? 12 : m - 1; setMesIni(mp); setMesFim(mp); }
    else if (chave === 'trimestre') { setMesIni(Math.max(1, m - 2)); setMesFim(m); }
    else if (chave === 'semestre') { setMesIni(Math.max(1, m - 5)); setMesFim(m); }
    else if (chave === 'ano') { setMesIni(1); setMesFim(12); }
    else if (chave === 'ytd') { setMesIni(1); setMesFim(m); }
  };

  const carregar = useCallback(async () => {
    setLoading({ cidades: true, clientes: true, classes: true });
    setBairrosExpand({});
    try {
      const [c, cl, cs] = await Promise.all([
        axios.get(`${API}/analises-venda/cidades`, { params }),
        axios.get(`${API}/analises-venda/clientes`, { params: { ...params, top: 30 } }),
        axios.get(`${API}/analises-venda/classes`, { params }),
      ]);
      setCidades(c.data);
      setClientes(cl.data);
      setVendaClasses(cs.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading({});
    }
  }, [params]);

  useEffect(() => { carregar(); }, [carregar]);

  const expandirBairros = async (cidade) => {
    if (bairrosExpand[cidade]) {
      // Toggle: se já expandido, fecha
      setBairrosExpand(prev => { const n = { ...prev }; delete n[cidade]; return n; });
      return;
    }
    setBairrosExpand(prev => ({ ...prev, [cidade]: { loading: true } }));
    try {
      const res = await axios.get(`${API}/analises-venda/bairros`, { params: { ...params, cidade } });
      setBairrosExpand(prev => ({ ...prev, [cidade]: res.data }));
    } catch (err) {
      setBairrosExpand(prev => ({ ...prev, [cidade]: { erro: 'Erro ao carregar bairros' } }));
    }
  };


  // Helpers UI
  const toggleFilial = (cod) => {
    setFiliaisSel(prev => prev.includes(cod) ? prev.filter(x => x !== cod) : [...prev, cod]);
  };
  const addClasse = (cls) => {
    if (!classesSel.includes(cls.clasprod)) setClassesSel([...classesSel, cls.clasprod]);
    // Não limpa o input — permite continuar selecionando outras categorias com nome similar
  };
  const removeClasse = (cls) => setClassesSel(classesSel.filter(c => c !== cls));
  // Só categorias nível 3 (6 caracteres). Quando o usuário filtrar por uma,
  // o backend pega tudo abaixo via LIKE prefix ('010101%' pega 010101, 01010110, etc.)
  const classesFiltradas = classes.filter(c =>
    String(c.clasprod || '').trim().length === 6 &&
    !classesSel.includes(c.clasprod) &&
    (c.clasprod.toLowerCase().includes(classeInput.toLowerCase()) || (c.descricao || '').toLowerCase().includes(classeInput.toLowerCase()))
  ).slice(0, 30);

  const ultAno = cidades?.anos[cidades.anos.length - 1];
  const totalGeralUlt = cidades?.linhas.reduce((a, l) => a + (l.anos[ultAno]?.venda || 0), 0) || 0;

  return (
    <div>
      {/* Cabeçalho com explicação */}
      <div className="mb-3">
        <h4 className="fw-bold mb-1"><i className="bi bi-graph-up-arrow me-2" style={{ color: COR }}></i>Análises de Venda</h4>
        <small className="text-muted">Mostra como a loja está vendendo por região, cliente e categoria. Use os filtros pra focar no que importa.</small>
      </div>

      {/* Filtros — organizado em 3 seções: PERÍODO, COMPARATIVO, FILTROS */}
      <div className="card p-3 mb-3">
        {/* SEÇÃO 1+2: PERÍODO + COMPARATIVO (mesma linha no desktop, empilha no mobile) */}
        <div className="mb-3">
          <div className="d-flex flex-wrap gap-3 align-items-start">
            {/* PERÍODO (esquerda — flex-grow) */}
            <div style={{ flex: '1 1 auto', minWidth: 300 }}>
              <div className="d-flex align-items-center mb-2 gap-2">
                <i className="bi bi-calendar3" style={{ color: COR }}></i>
                <strong style={{ fontSize: '0.85rem' }}>Período</strong>
                <Tooltip texto="Define qual intervalo de datas será analisado. Os atalhos cobrem os casos mais comuns. Use 'Datas específicas' se quiser DD/MM exato.">
                  <i className="bi bi-info-circle text-muted" style={{ fontSize: '0.85rem', cursor: 'help' }}></i>
                </Tooltip>
              </div>

              {/* Atalhos */}
              <div className="d-flex gap-1 flex-wrap mb-2">
                {[
                  { k: 'mes_atual', l: 'Mês atual' },
                  { k: 'mes_passado', l: 'Mês passado' },
                  { k: 'trimestre', l: 'Últimos 3 meses' },
                  { k: 'semestre', l: 'Últimos 6 meses' },
                  { k: 'ytd', l: 'Ano até hoje' },
                  { k: 'ano', l: 'Ano todo' },
                ].map(a => (
                  <button key={a.k} type="button" className="btn btn-sm btn-outline-secondary"
                    style={{ fontSize: '0.75rem', padding: '2px 10px' }}
                    onClick={() => aplicarAtalho(a.k)}>{a.l}</button>
                ))}
              </div>

              {/* Toggle modo + selects */}
              <div className="d-flex align-items-center gap-2 flex-wrap">
                <div className="btn-group btn-group-sm" role="group">
                  <button type="button"
                    className={`btn ${modoData === 'mes' ? 'btn-secondary' : 'btn-outline-secondary'}`}
                    style={{ fontSize: '0.78rem' }}
                    onClick={() => setModoData('mes')}>
                    Mês completo
                  </button>
                  <button type="button"
                    className={`btn ${modoData === 'datas' ? 'btn-secondary' : 'btn-outline-secondary'}`}
                    style={{ fontSize: '0.78rem' }}
                    onClick={() => setModoData('datas')}>
                    Datas específicas (DD/MM)
                  </button>
                </div>

                {modoData === 'mes' ? (
                  <>
                    <span className="text-muted" style={{ fontSize: '0.78rem' }}>de</span>
                    <select className="form-select form-select-sm" value={mesIni} onChange={e => setMesIni(Number(e.target.value))} style={{ width: 'auto' }}>
                      {MESES.map(m => <option key={m.v} value={m.v}>{m.l}</option>)}
                    </select>
                    <span className="text-muted" style={{ fontSize: '0.78rem' }}>até</span>
                    <select className="form-select form-select-sm" value={mesFim} onChange={e => setMesFim(Number(e.target.value))} style={{ width: 'auto' }}>
                      {MESES.map(m => <option key={m.v} value={m.v}>{m.l}</option>)}
                    </select>
                  </>
                ) : (
                  <>
                    <select className="form-select form-select-sm" value={diaIni} onChange={e => setDiaIni(Number(e.target.value))} style={{ width: 'auto' }}>
                      {Array.from({ length: ultimoDiaDoMes(mesIni) }, (_, i) => i + 1).map(d => <option key={d} value={d}>{d}</option>)}
                    </select>
                    <select className="form-select form-select-sm" value={mesIni} onChange={e => setMesIni(Number(e.target.value))} style={{ width: 'auto' }}>
                      {MESES.map(m => <option key={m.v} value={m.v}>{m.l}</option>)}
                    </select>
                    <span className="text-muted" style={{ fontSize: '0.78rem' }}>até</span>
                    <select className="form-select form-select-sm" value={diaFim} onChange={e => setDiaFim(Number(e.target.value))} style={{ width: 'auto' }}>
                      {Array.from({ length: ultimoDiaDoMes(mesFim) }, (_, i) => i + 1).map(d => <option key={d} value={d}>{d}</option>)}
                    </select>
                    <select className="form-select form-select-sm" value={mesFim} onChange={e => setMesFim(Number(e.target.value))} style={{ width: 'auto' }}>
                      {MESES.map(m => <option key={m.v} value={m.v}>{m.l}</option>)}
                    </select>
                  </>
                )}
              </div>
            </div>

            {/* COMPARATIVO (direita no desktop, embaixo no mobile) */}
            <div style={{ flex: '0 0 auto', minWidth: 280 }}>
              <div className="d-flex align-items-center mb-2 gap-2">
                <i className="bi bi-bar-chart-line" style={{ color: COR }}></i>
                <strong style={{ fontSize: '0.85rem' }}>Comparativo entre anos</strong>
                <Tooltip texto="Você pode ver só o ano atual, ou comparar com anos anteriores. Quando comparar, aparecem colunas com cada ano e a variação % (verde subiu, vermelho desceu).">
                  <i className="bi bi-info-circle text-muted" style={{ fontSize: '0.85rem', cursor: 'help' }}></i>
                </Tooltip>
              </div>
              <select className="form-select form-select-sm" value={qtdAnos} onChange={e => setQtdAnos(Number(e.target.value))} style={{ width: '100%' }}>
                <option value="1">Apenas o ano atual (sem comparativo)</option>
                <option value="2">Comparar com o ano anterior (2 anos)</option>
                <option value="3">Comparar 3 anos</option>
                <option value="4">Comparar 4 anos</option>
                <option value="5">Comparar 5 anos</option>
              </select>
            </div>
          </div>
        </div>

        <hr className="my-2" />

        {/* SEÇÃO 3: FILTROS DE LOJA E PRODUTO */}
        <div>
          <div className="d-flex align-items-center mb-2 gap-2">
            <i className="bi bi-funnel" style={{ color: COR }}></i>
            <strong style={{ fontSize: '0.85rem' }}>Filtrar</strong>
          </div>

          <div className="row g-3">
            {/* Filiais */}
            <div className="col-md-6">
              <label className="form-label d-flex align-items-center gap-1" style={{ fontSize: '0.78rem' }}>
                Lojas
                <Tooltip texto={isGerenteFilial ? 'Você só pode ver os dados da sua loja.' : 'Marque uma ou mais lojas. Se nenhuma estiver marcada, mostra todas.'}>
                  <i className="bi bi-info-circle text-muted" style={{ fontSize: '0.78rem', cursor: 'help' }}></i>
                </Tooltip>
              </label>
              {isGerenteFilial ? (
                <div className="badge" style={{ background: COR, fontSize: '0.82rem', padding: '6px 12px' }}>
                  <i className="bi bi-shop me-1"></i>{filiais.find(f => String(f.codigo) === String(user.filial_id))?.nome || `Loja ${user.filial_id}`}
                </div>
              ) : (
                <div className="d-flex flex-wrap gap-1">
                  {filiais.map(f => {
                    const cod = String(f.codigo).padStart(2, '0');
                    const sel = filiaisSel.includes(cod);
                    return (
                      <button key={cod} type="button"
                        className="btn btn-sm" style={{
                          background: sel ? COR : '#fff', color: sel ? '#fff' : COR,
                          border: `1px solid ${COR}`, padding: '2px 10px', fontSize: '0.78rem',
                        }}
                        onClick={() => toggleFilial(cod)}>{f.nome}</button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Classes */}
            <div className="col-md-6" ref={classeRef}>
              <label className="form-label d-flex align-items-center gap-1" style={{ fontSize: '0.78rem' }}>
                Categorias de produto
                <Tooltip texto="Filtra as vendas pelas categorias escolhidas. Mostramos só categorias nível 3 (ex: PORCELANATO, CIMENTOS) — quando você escolhe uma, o filtro pega ela e todas as subcategorias abaixo (ex: 'Porcelanato' inclui 'Porcelanato Nacional', 'Porcelanato Importado' etc.). Se vazio, mostra todas.">
                  <i className="bi bi-info-circle text-muted" style={{ fontSize: '0.78rem', cursor: 'help' }}></i>
                </Tooltip>
              </label>
              <div style={{ position: 'relative' }}>
                <input className="form-control form-control-sm"
                  placeholder="Buscar categoria (digite código ou nome)..."
                  value={classeInput}
                  onChange={e => { setClasseInput(e.target.value); setClasseDropOpen(true); }}
                  onFocus={() => setClasseDropOpen(true)} />
                {classeDropOpen && (
                  <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, maxHeight: 240, overflowY: 'auto', background: '#fff', border: '1px solid #ddd', borderRadius: 4, zIndex: 10, marginTop: 2 }}>
                    {classesFiltradas.map(c => (
                      <div key={c.clasprod} style={{ padding: '6px 12px', cursor: 'pointer', borderBottom: '1px solid #eee', fontSize: '0.82rem' }}
                        onClick={() => addClasse(c)}
                        onMouseEnter={e => e.currentTarget.style.background = '#f8f6f0'}
                        onMouseLeave={e => e.currentTarget.style.background = '#fff'}>
                        <strong>{c.clasprod}</strong> — {c.descricao}
                      </div>
                    ))}
                    {classesFiltradas.length === 0 && <div className="p-2 text-muted text-center" style={{ fontSize: '0.82rem' }}>Sem resultados</div>}
                  </div>
                )}
              </div>
              {classesSel.length > 0 && (
                <div className="mt-2 d-flex flex-wrap gap-1">
                  {classesSel.map(cls => {
                    const info = classes.find(x => x.clasprod === cls);
                    return (
                      <span key={cls} className="badge-filial d-flex align-items-center gap-1">
                        <strong>{cls}</strong> {info?.descricao || ''}
                        <button className="btn btn-sm p-0 ms-1" onClick={() => removeClasse(cls)} style={{ lineHeight: 1 }}><i className="bi bi-x"></i></button>
                      </span>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Aviso explicativo geral */}
      <div className="alert py-2 mb-3" style={{ background: '#F5EBF2', border: `1px solid ${COR}`, fontSize: '0.78rem', color: '#444' }}>
        <i className="bi bi-info-circle me-1" style={{ color: COR }}></i>
        <strong>Como os números são calculados:</strong> pedidos faturados (com desconto aplicado), em 3 tipos de venda: <strong>Caixa</strong> (cliente leva na hora), <strong>Expedição</strong> (retira depois) e <strong>Futura</strong> (entrega no endereço). Cada análise abaixo usa o que faz mais sentido — leia a explicação <i className="bi bi-info-circle"></i> de cada card. <strong>Não desconta</strong> devoluções nem cancelamentos.
      </div>

      {/* Card 1: Cidades / Bairros */}
      <div className="card p-3 mb-3">
        <div className="d-flex align-items-center justify-content-between mb-2 flex-wrap gap-2">
          <div className="d-flex align-items-center gap-2">
            <h6 className="fw-bold mb-0"><i className="bi bi-geo me-2" style={{ color: COR }}></i>Vendas por cidade</h6>
            <Tooltip texto="Quanto foi entregue em cada cidade. Considera APENAS Venda Futura (entrega no endereço do cliente) — Caixa e Expedição ficam de fora aqui porque o cliente vem até a loja, não tem 'cidade de venda' geográfica. Clique em uma cidade pra ver os bairros.">
              <i className="bi bi-info-circle text-muted" style={{ fontSize: '0.85rem', cursor: 'help' }}></i>
            </Tooltip>
          </div>
          {cidades && <small className="text-muted">{cidades.linhas.length} cidades — total {ultAno}: <strong>R$ {fmt(totalGeralUlt)}</strong></small>}
        </div>
        <div className="alert py-1 px-2 mb-2" style={{ background: '#F5EBF2', border: `1px solid ${COR}`, fontSize: '0.75rem', color: '#444' }}>
          <i className="bi bi-info-circle me-1" style={{ color: COR }}></i>
          Considera <strong>apenas Venda Futura</strong> (entrega no endereço). Caixa e Expedição não aparecem aqui — o cliente vai até a loja nesses casos.
        </div>
        {loading.cidades && <div className="text-center p-3"><span className="spinner-border spinner-border-sm" /></div>}
        {cidades && !loading.cidades && (
          <div className="table-responsive">
            <TabelaMultiAno data={cidades} chave="cidade" extras={(l) => l.uf}
              mostrarTotal
              onClickLinha={(c) => expandirBairros(c.cidade)}
              expandido={(c) => bairrosExpand[c.cidade]}
              renderExpandido={(c) => {
                const e = bairrosExpand[c.cidade];
                if (e?.loading) return <div className="p-3 text-muted small" style={{ background: '#F5EBF2', borderLeft: `4px solid ${COR}` }}><span className="spinner-border spinner-border-sm me-2" />Carregando bairros...</div>;
                if (e?.aviso) return <div className="p-3 small" style={{ background: '#FFF3CD', borderLeft: `4px solid #FFE69C`, color: '#664D03' }}><i className="bi bi-info-circle me-1"></i>{e.aviso}</div>;
                if (e?.erro) return <div className="p-3 text-danger small" style={{ background: '#F5EBF2', borderLeft: `4px solid ${COR}` }}>{e.erro}</div>;
                return (
                  <div style={{ background: '#F5EBF2', borderLeft: `4px solid ${COR}`, padding: '8px 8px 8px 32px' }}>
                    <div className="d-flex align-items-center mb-2" style={{ fontSize: '0.78rem', color: COR, fontWeight: 600 }}>
                      <i className="bi bi-arrow-return-right me-2"></i>
                      Bairros de <strong className="ms-1">{c.cidade}</strong>
                    </div>
                    <TabelaMultiAno data={e} chave="bairro" pequena bairroStyle mostrarTotal />
                  </div>
                );
              }} />
          </div>
        )}
      </div>

      {/* Card 2: Top Clientes */}
      <div className="card p-3 mb-3">
        <div className="d-flex align-items-center gap-2 mb-2">
          <h6 className="fw-bold mb-0"><i className="bi bi-people me-2" style={{ color: COR }}></i>Top clientes</h6>
          <Tooltip texto="Os 30 clientes que mais compraram no período. Considera os 3 tipos de venda (Caixa + Expedição + Futura) — afinal o que importa aqui é quem comprou, não como recebeu. Não aparece 'Consumidor Final' (venda sem cadastro). Clique no cabeçalho de qualquer coluna pra ordenar diferente.">
            <i className="bi bi-info-circle text-muted" style={{ fontSize: '0.85rem', cursor: 'help' }}></i>
          </Tooltip>
        </div>
        <div className="alert py-1 px-2 mb-2" style={{ background: '#F5EBF2', border: `1px solid ${COR}`, fontSize: '0.75rem', color: '#444' }}>
          <i className="bi bi-info-circle me-1" style={{ color: COR }}></i>
          Inclui <strong>Caixa + Expedição + Futura</strong> (tudo que o cliente comprou).
        </div>
        {loading.clientes && <div className="text-center p-3"><span className="spinner-border spinner-border-sm" /></div>}
        {clientes && !loading.clientes && (
          <div className="table-responsive">
            <TabelaMultiAno data={clientes} chave="razao" extras={(l) => l.cidade} pequena />
          </div>
        )}
      </div>

      {/* Card 3: Vendas por classe */}
      <div className="card p-3 mb-3">
        <div className="d-flex align-items-center gap-2 mb-2">
          <h6 className="fw-bold mb-0"><i className="bi bi-tags me-2" style={{ color: COR }}></i>Vendas por categoria de produto</h6>
          <Tooltip texto="Quanto cada categoria nível 3 de produto vendeu (porcelanato, telhas, cimentos etc.). Subcategorias mais específicas (tipo 'Porcelanato Nacional') são agrupadas na categoria pai. Considera os 3 tipos de venda (Caixa + Expedição + Futura) e todas as lojas selecionadas, sem separar por região.">
            <i className="bi bi-info-circle text-muted" style={{ fontSize: '0.85rem', cursor: 'help' }}></i>
          </Tooltip>
        </div>
        <div className="alert py-1 px-2 mb-2" style={{ background: '#F5EBF2', border: `1px solid ${COR}`, fontSize: '0.75rem', color: '#444' }}>
          <i className="bi bi-info-circle me-1" style={{ color: COR }}></i>
          Inclui <strong>Caixa + Expedição + Futura</strong>, todas as lojas selecionadas, sem separação geográfica. Categorias agrupadas em <strong>nível 3</strong> (subcategorias rolam pra cima).
        </div>
        {loading.classes && <div className="text-center p-3"><span className="spinner-border spinner-border-sm" /></div>}
        {vendaClasses && !loading.classes && (
          <div className="table-responsive">
            <TabelaMultiAno data={vendaClasses} chave="classe_nome" extras={(l) => l.clasprod} pequena mostrarTotal />
          </div>
        )}
      </div>

    </div>
  );
}

// Tabela genérica multi-ano com colunas YoY + paginação
function TabelaMultiAno({ data, chave, extras, pequena, onClickLinha, expandido, renderExpandido, paginavel = true, defaultPorPagina = 25, bairroStyle = false, mostrarTotal = false }) {
  const fontSize = pequena ? '0.78rem' : '0.85rem';
  const [pagina, setPagina] = useState(1);
  const [porPagina, setPorPagina] = useState(defaultPorPagina);
  // Sort: { campo: 'chave' | 'ano:2024' | 'yoy:2024_2025', dir: 'asc'|'desc' }
  // Default: maior venda do ano mais recente
  const ultAno = data.anos[data.anos.length - 1];
  const [sort, setSort] = useState({ campo: `ano:${ultAno}`, dir: 'desc' });
  const totalLinhas = data.linhas.length;

  // Reset pra página 1 quando os dados mudam
  useEffect(() => { setPagina(1); }, [totalLinhas, sort.campo, sort.dir]);

  // Ordena
  const linhasOrd = useMemo(() => {
    const arr = [...data.linhas];
    const dir = sort.dir === 'asc' ? 1 : -1;
    arr.sort((a, b) => {
      let va, vb;
      if (sort.campo === 'chave') {
        va = String(a[chave] || '').toLowerCase(); vb = String(b[chave] || '').toLowerCase();
        return va.localeCompare(vb) * dir;
      }
      if (sort.campo.startsWith('ano:')) {
        const ano = sort.campo.slice(4);
        va = a.anos[ano]?.venda || 0; vb = b.anos[ano]?.venda || 0;
      } else if (sort.campo.startsWith('yoy:')) {
        const par = sort.campo.slice(4);
        va = a.yoys?.[par] ?? -Infinity; vb = b.yoys?.[par] ?? -Infinity;
      }
      return (va - vb) * dir;
    });
    return arr;
  }, [data.linhas, sort, chave]);

  const totalPaginas = Math.max(1, Math.ceil(totalLinhas / porPagina));
  const linhasPag = paginavel ? linhasOrd.slice((pagina - 1) * porPagina, pagina * porPagina) : linhasOrd;
  const colSpan = data.anos.length + (data.anos.length - 1) + 1;
  const labelChave = chave === 'cidade' ? 'Cidade / UF' : chave === 'bairro' ? 'Bairro' : chave === 'razao' ? 'Cliente' : 'Classe';
  const headerBg = bairroStyle ? '#E0D5DD' : '#F5EBF2';

  const toggleSort = (campo) => {
    setSort(prev => prev.campo === campo
      ? { campo, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
      : { campo, dir: 'desc' }); // primeira vez, ordena DESC (maior primeiro)
  };

  const SortIcon = ({ campo }) => {
    if (sort.campo !== campo) return <i className="bi bi-arrow-down-up ms-1" style={{ fontSize: '0.65rem', opacity: 0.4 }}></i>;
    return <i className={`bi bi-arrow-${sort.dir === 'asc' ? 'up' : 'down'} ms-1`} style={{ fontSize: '0.7rem', color: COR }}></i>;
  };

  const thStyleBase = {
    cursor: 'pointer', userSelect: 'none',
    fontSize: bairroStyle ? '0.72rem' : undefined,
    textTransform: bairroStyle ? 'uppercase' : undefined,
    letterSpacing: bairroStyle ? '0.05em' : undefined,
    color: bairroStyle ? COR : undefined,
  };

  return (
    <>
      <table className={`table table-sm mb-0 ${bairroStyle ? '' : 'table-hover'}`} style={{ fontSize, background: bairroStyle ? '#fff' : undefined, borderRadius: bairroStyle ? 4 : undefined, overflow: 'hidden' }}>
        <thead style={{ background: headerBg }}>
          <tr>
            <th style={{ minWidth: 180, ...thStyleBase }} onClick={() => toggleSort('chave')}>
              {labelChave}<SortIcon campo="chave" />
            </th>
            {data.anos.map(a => (
              <th key={a} style={{ textAlign: 'right', minWidth: 100, ...thStyleBase }} onClick={() => toggleSort(`ano:${a}`)}>
                {a}<SortIcon campo={`ano:${a}`} />
              </th>
            ))}
            {data.anos.length > 1 && data.anos.slice(1).map((a, i) => {
              const ant = data.anos[i];
              const par = `${ant}_${a}`;
              return (
                <th key={`yoy-${par}`} style={{ textAlign: 'right', minWidth: 80, ...thStyleBase, color: bairroStyle ? COR : '#666' }} onClick={() => toggleSort(`yoy:${par}`)}>
                  YoY {ant}→{a}<SortIcon campo={`yoy:${par}`} />
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {linhasPag.length === 0 && <tr><td colSpan={colSpan} className="text-center text-muted py-3">Sem dados no período</td></tr>}
          {linhasPag.map((l, idx) => {
            const exp = expandido?.(l);
            const keyId = `${l[chave]}__${extras ? extras(l) : ''}__${idx}`;
            return (
              <React.Fragment key={keyId}>
                <tr style={{ cursor: onClickLinha ? 'pointer' : 'default', background: bairroStyle && idx % 2 === 1 ? '#F9F4F7' : undefined }} onClick={() => onClickLinha?.(l)}>
                  <td>
                    {onClickLinha && <i className={`bi bi-chevron-${exp ? 'down' : 'right'} me-1`} style={{ color: COR }}></i>}
                    {bairroStyle && <i className="bi bi-geo-alt me-1" style={{ color: COR, opacity: 0.5 }}></i>}
                    <span style={{ fontWeight: bairroStyle ? 500 : 700, color: bairroStyle ? '#444' : undefined }}>{l[chave]}</span>
                    {extras && <small className="text-muted ms-2">{extras(l)}</small>}
                  </td>
                  {data.anos.map(a => (
                    <td key={a} style={{ textAlign: 'right' }}>R$ {fmt(l.anos[a]?.venda || 0)}</td>
                  ))}
                  {data.anos.slice(1).map((a, i) => {
                    const ant = data.anos[i];
                    const yoy = l.yoys?.[`${ant}_${a}`];
                    return <td key={`y-${ant}-${a}`} style={{ textAlign: 'right', color: corPct(yoy), fontWeight: 600 }}>{fmtPct(yoy)}</td>;
                  })}
                </tr>
                {exp && renderExpandido && (
                  <tr><td colSpan={colSpan} className="p-0">
                    {renderExpandido(l)}
                  </td></tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
        {mostrarTotal && data.linhas.length > 0 && (() => {
          // Totais sobre TODAS as linhas (não só a página atual)
          const totaisAno = {};
          data.anos.forEach(a => {
            totaisAno[a] = data.linhas.reduce((s, l) => s + (l.anos[a]?.venda || 0), 0);
          });
          // YoY do total
          const yoysTotal = {};
          for (let i = 1; i < data.anos.length; i++) {
            const ant = data.anos[i - 1], at = data.anos[i];
            const va = totaisAno[ant], vb = totaisAno[at];
            yoysTotal[`${ant}_${at}`] = va > 0 ? ((vb - va) / va) * 100 : null;
          }
          return (
            <tfoot style={{ background: headerBg, fontWeight: 700 }}>
              <tr>
                <td>TOTAL</td>
                {data.anos.map(a => <td key={a} style={{ textAlign: 'right' }}>R$ {fmt(totaisAno[a])}</td>)}
                {data.anos.slice(1).map((a, i) => {
                  const ant = data.anos[i];
                  const yoy = yoysTotal[`${ant}_${a}`];
                  return <td key={`yoy-tot-${ant}-${a}`} style={{ textAlign: 'right', color: corPct(yoy) }}>{fmtPct(yoy)}</td>;
                })}
              </tr>
            </tfoot>
          );
        })()}
      </table>
      {paginavel && totalLinhas > 0 && (
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mt-2 pt-2" style={{ borderTop: '1px solid #eee' }}>
          <div className="d-flex align-items-center gap-2" style={{ fontSize: '0.78rem' }}>
            <span className="text-muted">Mostrando {(pagina - 1) * porPagina + 1}–{Math.min(pagina * porPagina, totalLinhas)} de {totalLinhas}</span>
            <span className="text-muted">·</span>
            <span className="text-muted">Por página:</span>
            <select className="form-select form-select-sm" style={{ width: 'auto', fontSize: '0.78rem', padding: '1px 24px 1px 6px' }}
              value={porPagina} onChange={e => { setPorPagina(Number(e.target.value)); setPagina(1); }}>
              <option value="10">10</option>
              <option value="25">25</option>
              <option value="50">50</option>
              <option value="100">100</option>
              <option value="999999">Todas</option>
            </select>
          </div>
          {totalPaginas > 1 && (
            <div className="d-flex align-items-center gap-1">
              <button className="btn btn-sm btn-outline-secondary" disabled={pagina === 1} onClick={() => setPagina(1)} style={{ padding: '2px 8px' }}><i className="bi bi-chevron-double-left"></i></button>
              <button className="btn btn-sm btn-outline-secondary" disabled={pagina === 1} onClick={() => setPagina(p => p - 1)} style={{ padding: '2px 8px' }}><i className="bi bi-chevron-left"></i></button>
              <span style={{ fontSize: '0.78rem', minWidth: 80, textAlign: 'center' }}>Página {pagina} de {totalPaginas}</span>
              <button className="btn btn-sm btn-outline-secondary" disabled={pagina === totalPaginas} onClick={() => setPagina(p => p + 1)} style={{ padding: '2px 8px' }}><i className="bi bi-chevron-right"></i></button>
              <button className="btn btn-sm btn-outline-secondary" disabled={pagina === totalPaginas} onClick={() => setPagina(totalPaginas)} style={{ padding: '2px 8px' }}><i className="bi bi-chevron-double-right"></i></button>
            </div>
          )}
        </div>
      )}
    </>
  );
}
